/**
 * Tenant resolution from the address bar (brand/white-label.md: before first paint).
 *
 * 1. Preview hosts (`localhost`, `127.0.0.1`, `*.pages.dev`): `?tenant=<slug>`, else the build's default tenant.
 * 2. `<slug>.<root domain>`, e.g. `thomson-airborne.cloud.teslyn.au`.
 * 3. A custom domain mapped to a slug, e.g. `cloud.thomsonair.com.au`.
 * Anything else is an unknown portal address (null): we never guess a tenant on a real hostname.
 */
export interface ResolveOptions {
  readonly defaultTenant: string;
  readonly rootDomains: readonly string[];
  readonly customDomains: ReadonlyMap<string, string>;
}

export type TenantSource = 'query' | 'default' | 'subdomain' | 'custom-domain';

export interface ResolvedSlug {
  readonly slug: string;
  readonly source: TenantSource;
}

const SLUG = /^[a-z0-9][a-z0-9-]{1,62}$/;

export function isPreviewHost(hostname: string): boolean {
  const h = hostname.toLowerCase();
  return h === 'localhost' || h === '127.0.0.1' || h === '[::1]' || h.endsWith('.pages.dev');
}

export function resolveTenantSlug(
  hostname: string,
  search: string,
  opts: ResolveOptions,
): ResolvedSlug | null {
  const host = hostname.toLowerCase().replace(/\.$/, '');
  if (isPreviewHost(host)) {
    const q = new URLSearchParams(search).get('tenant')?.trim().toLowerCase() ?? '';
    if (SLUG.test(q)) return { slug: q, source: 'query' };
    return SLUG.test(opts.defaultTenant) ? { slug: opts.defaultTenant, source: 'default' } : null;
  }
  const custom = opts.customDomains.get(host);
  if (custom && SLUG.test(custom)) return { slug: custom, source: 'custom-domain' };
  for (const root of opts.rootDomains) {
    if (host.endsWith(`.${root}`)) {
      const label = host.slice(0, -(root.length + 1));
      if (!label.includes('.') && SLUG.test(label)) return { slug: label, source: 'subdomain' };
    }
  }
  return null;
}
